import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import Carousel from '@hpstellar/core/Carousel';

import { Helpers } from '../../core/src/helpers';
import CartQuantityIndicator from './cart-quantity-indicator';
import PurchaseCTA from './purchase-cta';
import PurchaseSummary from './purchase-summary';
import ModalFooterV2 from './modal-footer-v2';
import CrossSellProductCard from '../../product/components/cross-sell-product-card-v2';
import Icon from '../../ui/components/icon';
import withPageComponents from '../../page/components/with-page-components';
import withWindowResize from '../../ui/components/window-resize';
import { withAddToCart } from '../../product/components/purchase-button-container';
import DropDown from '../../shared/components/dropdown';
import Link from '@hpstellar/core/Link';

import '../css/tabs-xsell-v2.less';

const { memoize } = Helpers;

const getControls = type => {
    if (type === 'category') {
        return {
            slidesToShow: 6,
            slidesToScroll: 6,
            infinite: false,
            responsive: [
                {
                    breakpoint: 950,
                    settings: {
                        slidesToShow: 5,
                        slidesToScroll: 5,
                    },
                },
                {
                    breakpoint: 767,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 2,
                    },
                },
                {
                    breakpoint: 620,
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                    },
                },
            ],
        };
    } else if (type === 'product') {
        return {
            slidesToShow: 4,
            slidesToScroll: 4,
            infinite: false,
            dots: true,
            responsive: [
                {
                    breakpoint: 1400,
                    settings: {
                        slidesToShow: 3,
                        slidesToScroll: 3,
                    },
                },
                {
                    breakpoint: 763,
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                        dots: true,
                    },
                },
                {
                    breakpoint: 500,
                    settings: {
                        slidesToShow: 1.4,
                        slidesToScroll: 1,
                        dots: true,
                    },
                },
                {
                    breakpoint: 350,
                    settings: {
                        slidesToShow: 1.2,
                        slidesToScroll: 1,
                        dots: true,
                    },
                },
            ],
        };
    }
};

const createMapper = categories => {
    const obj = {};
    categories.forEach(category => {
        obj[category._id] = 0;
    });
    return obj;
};

const usePrevious = value => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

const TabsXSellModalV2 = ({
    preAddedProducts,
    pdpCrossSell,
    product,
    closeModal,
    device,
    width,
    ctaMetrics,
    getProductClickMetrics,
    xsellType,
    addMultipleToCart,
    updateCart,
    cartItems,
    overwrites,
}) => {
    const { relatedProducts, categories } = pdpCrossSell || {};
    const xsellProducts = overwrites && overwrites.length > 0 ? overwrites : relatedProducts;
    const { isSmb } = product;
    const isProductPC = Helpers.isProductPC(product);
    const mapper = createMapper(categories);
    const [skusAdded, setSkusAdded] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [precheckedAdded, setPrecheckedAdded] = useState(false);
    const [categoryMapper, setCategoryMapper] = useState(mapper);
    const [preAddedRemoved, setPreAddedRemoved] = useState(false);
    const previousSkusAdded = usePrevious(skusAdded) || [];

    useEffect(() => {
        if (!precheckedAdded) {
            if (preAddedProducts && preAddedProducts.length > 0) {
                addMultipleToCart(preAddedProducts, 1).then(() => {
                    setSkusAdded([...skusAdded, ...preAddedProducts.map(prod => prod.sku)]);
                    setPrecheckedAdded(true);
                });
            }
        }
    }, []);

    useEffect(() => {
        if (skusAdded.length > previousSkusAdded.length) {
            updateQuantity('add');
        } else if (skusAdded.length < previousSkusAdded.length) {
            updateQuantity('remove');
        }
    }, [skusAdded]);

    const catSettings = {
        ...getControls('category'),
    };
    const prodSettings = {
        ...getControls('product', ((categories[activeTab] || {}).products || []).length),
    };

    const handleAddandRemove = (action, sku) => {
        if (action === 'add') {
            setSkusAdded([sku, ...skusAdded]);
        } else if (action === 'remove') {
            setSkusAdded(skusAdded.filter(prodSku => prodSku !== sku));
        }
    };

    const removePreAdded = () => {
        /*temp, hardcoded, updateCart doesn't work with promise.all*/
        /*preAddedProducts length can only be 1 or 2*/
        if (preAddedProducts.length > 0) {
            const product1 = preAddedProducts[0];
            const itemToRemove1 = cartItems.filter(item => item.pNum === product1.sku)[0] || {};
            const quantityToRemove1 = (itemToRemove1 || {}).qty - 1 || 0;
            const mappedPreAdded = preAddedProducts.map(product => product.sku);
            updateCart(itemToRemove1, quantityToRemove1).then(() => {
                if (preAddedProducts.length > 1) {
                    const product2 = preAddedProducts[1];
                    const itemToRemove2 = cartItems.filter(item => item.pNum === product2.sku)[0] || {};
                    const quantityToRemove2 = (itemToRemove2 || {}).qty - 1 || 0;
                    updateCart(itemToRemove2, quantityToRemove2);
                }
                setPreAddedRemoved(true);
                setSkusAdded(skusAdded.filter(prodSku => !mappedPreAdded.includes(prodSku)));
            });
        }
    };

    const updateQuantity = action => {
        let obj = {};
        let changedProducts =
            action === 'add'
                ? xsellProducts.filter(product => skusAdded.includes(product.sku))
                : xsellProducts.filter(
                      product => previousSkusAdded.includes(product.sku) && !skusAdded.includes(product.sku)
                  );

        changedProducts.forEach(product => {
            product.categories.forEach(category => {
                if (category._id in categoryMapper) {
                    if (action === 'add') {
                        if (!obj.hasOwnProperty(category._id)) {
                            obj[category._id] = 1;
                        } else {
                            obj[category._id]++;
                        }
                    } else if (action === 'remove') {
                        if (!obj.hasOwnProperty(category._id)) {
                            obj[category._id] = categoryMapper[category._id] - 1;
                        } else {
                            obj[category._id]--;
                        }
                    }
                }
            });
        });
        setCategoryMapper({
            ...categoryMapper,
            ...obj,
        });
    };

    const filteredProducts = memoize(
        (index, products, categories) => {
            if (categories.length === 0) {
                return products;
            }

            let filteredProducts;

            if (preAddedProducts && preAddedProducts.length > 0) {
                const preAddedSkus = preAddedProducts.map(product => product.sku);
                filteredProducts = [
                    ...preAddedProducts,
                    ...products.filter(
                        (product, i) =>
                            !preAddedSkus.includes(product.sku) &&
                            product.categories.some(cat => cat._id === (categories[activeTab] || {})._id)
                    ),
                ].filter(product => product.categories.some(cat => cat._id === (categories[activeTab] || {})._id));
            } else {
                filteredProducts = products.filter((product, i) =>
                    product.categories.some(cat => cat._id === (categories[activeTab] || {})._id)
                );
            }
            return filteredProducts;
        },
        (index, categories) => {
            const products = (pdpCrossSell || {}).relatedProducts;
            return `${index}-${products.length}`;
        }
    );

    const mapSummaryProduct = products => {
        /*native filter can't be used here to maintain order*/
        const filteredProducts = [];

        skusAdded.forEach(sku => {
            products.forEach(product => {
                if (product.sku === sku) {
                    filteredProducts.push(product);
                }
            });
        });

        return filteredProducts;
    };

    const categoryProducts = filteredProducts(activeTab, xsellProducts, categories);
    const selectedCategory = categories[activeTab];
    const withPreAdded = preAddedProducts && preAddedProducts.length > 0 && !preAddedRemoved;

    const content =
        categories.length > 0 ? (
            <div
                className={`tabs-v2-wrapper${isProductPC ? ' pc' : ''}${isSmb === 'Yes' ? ' business' : ''}${
                    withPreAdded ? ' preadd' : ''
                }`}
            >
                <button className="close" onClick={closeModal}>
                    <Icon name="x" size={15} />
                </button>
                <CartQuantityIndicator device={device} className="tabs-v2" />
                <div className="modal-content-wrapper">
                    <PurchaseSummary
                        products={[...mapSummaryProduct(xsellProducts), product]}
                        handleRemove={handleAddandRemove}
                        closeModal={closeModal}
                        device={device}
                        width={width}
                    />
                    {width <= 768 && (
                        <ModalFooterV2
                            closeModal={closeModal}
                            className="tabs-v2"
                            cartBtnText="go to cart"
                            cartBtnMobileText="go to cart"
                            backBtnMobileText="continue shopping"
                            backBtnText="continue shopping"
                            device={device}
                        />
                    )}
                    {withPreAdded && (
                        <div className="disclaimer">
                            <h3>
                                For your convenience, we are adding our most popular back-up cartridges to your printer
                                order
                            </h3>
                            <Link onClick={removePreAdded}>No thanks, please remove cartridges</Link>
                        </div>
                    )}
                    <div className={`products-section${withPreAdded ? ' with-preadd' : ''}`}>
                        <div className="tabs-v2-category-list">
                            {width > 768 ? (
                                <Carousel
                                    className="xsell-popup-category-carousel-v2"
                                    prevArrowClassName="xsell-cats-prev"
                                    nextArrowClassName="xsell-cats-next"
                                    {...catSettings}
                                >
                                    {categories.map((category, i) => {
                                        const quantity = categoryMapper[category._id];
                                        return (
                                            <div
                                                key={category + '-' + i}
                                                className={`cat-item${
                                                    category._id === selectedCategory._id ? ' selected' : ''
                                                }`}
                                                onClick={() => setActiveTab(i)}
                                            >
                                                {quantity > 0 && <span className="quantity">{quantity}</span>}
                                                <Icon name={category.iconName} size={50} />
                                                <p className="cat-title">{category.displayName}</p>
                                            </div>
                                        );
                                    })}
                                </Carousel>
                            ) : (
                                <DropDown
                                    className="cross-sell-mobile-dropdown modal"
                                    options={categories}
                                    displayKey={'displayName'}
                                    initialValue={activeTab}
                                    selectOption={setActiveTab}
                                />
                            )}
                        </div>
                        <div className="tabs-v2-xsell-products">
                            <Carousel
                                prevArrowClassName="xsell-prds-prev"
                                nextArrowClassName="xsell-prds-next"
                                {...prodSettings}
                            >
                                {categoryProducts.map( (product, idx) => (
                                    <CrossSellProductCard
                                        key={product.sku}
                                        className="xsell-modal"
                                        product={product}
                                        ctaMetrics={ctaMetrics}
                                        crossSellMethod={xsellType}
                                        position={idx + 1}
                                        customCTA={
                                            <PurchaseCTA
                                                product={product}
                                                handleAddandRemove={handleAddandRemove}
                                                added={skusAdded.indexOf(product.sku) > -1}
                                                ctaMetrics={ctaMetrics}
                                                xsellTemplate={xsellType}
                                            />
                                        }
                                        width={width}
                                    />
                                ))}
                            </Carousel>
                            {preAddedProducts && preAddedProducts.length > 0 && !preAddedRemoved && (
                                <div className="disclaimer bottom">
                                    Get up to 2x the pages with Original HP XL inks vs. standard cartridges
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        ) : null;
    return content;
};

const mapStateToProps = state => ({
    cartItems: state.cartInfo.items,
});

const component = withWindowResize(
    withPageComponents(withAddToCart(TabsXSellModalV2), { components: ['pdpCrossSell', 'product'] })
);
export default connect(mapStateToProps, null)(component);
